<template>
    <div>
        <v-hover v-slot="{ isHovering, props }">
            <v-card
                v-bind="props"
                :elevation="isHovering ? 4 : 0"
                :class="
                    $vuetify.display.mobile
                        ? 'default_card_mobile dashboard_card d-flex flex-column ma-0'
                        : 'default_card dashboard_card d-flex flex-column ma-0'
                "
                :style="$vuetify.display.mobile ? 'height: 100%' : 'height: 250px'"
            >
                <v-row no-gutters>
                    <v-col cols="12" class="title ma-2"> Trésorerie </v-col>
                    <v-col v-if="isTotalBalanceLoading" cols="12" class="px-4">
                        <v-skeleton-loader type="card"></v-skeleton-loader>
                    </v-col>
                    <v-col v-if="!isTotalBalanceLoading" cols="12" class="px-4">
                        <v-row no-gutters v-if="!vueGraph">
                            <v-col
                                :cols="$vuetify.display.mdAndDown ? '12' : '7'"
                                class="d-flex justify-center align-center"
                            >
                                <h1>
                                    {{
                                        totalLastReconciledBalance
                                            ? newCommonFormatCurrency(
                                                  totalLastReconciledBalance?.balance,
                                                  undefined,
                                              )
                                            : "-"
                                    }}
                                </h1>
                            </v-col>
                            <v-col
                                class="d-flex justify-end align-center"
                                :cols="$vuetify.display.mdAndDown ? '12' : '5'"
                            >
                                <v-row no-gutters>
                                    <v-col cols="4" class="d-flex">Entrées </v-col>
                                    <v-col cols="8" class="d-flex font-weight-bold justify-end">
                                        <v-icon class="mx-1" color="green" size="x-small"
                                            >$fa-caret-up</v-icon
                                        >
                                        <span class="text-green">
                                            {{
                                                newCommonFormatCurrency(
                                                    bankTransactionAggregate.amountDebit,
                                                    undefined,
                                                )
                                            }}</span
                                        >
                                    </v-col>
                                    <v-col cols="4" class="d-flex">Sorties </v-col>
                                    <v-col cols="8" class="d-flex font-weight-bold justify-end">
                                        <v-icon class="mx-1" color="new_red" size="x-small"
                                            >$fa-caret-down</v-icon
                                        >
                                        <span class="text-red">{{
                                            newCommonFormatCurrency(
                                                bankTransactionAggregate.amountCredit,
                                                undefined,
                                            )
                                        }}</span>
                                    </v-col>
                                    <v-col cols="12" class="d-flex mt-2">
                                        Entrées/sorties sur le dernier mois
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                        <v-row no-gutters class="d-flex justify-center" v-else>
                            <v-col cols="8">
                                <bar-chart
                                    v-if="chartData"
                                    :data="chartData"
                                    :options="options"
                                    :height="$vuetify.display.mobile ? 100 : 140"
                                    :width="200"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col v-if="!isTotalBalanceLoading" cols="12" class="mt-auto">
                        <v-row no-gutters class="px-2 pt-2 pb-0">
                            <v-col cols="5" class="d-flex justify-center"> </v-col>
                            <v-col cols="2" class="d-flex justify-center">
                                <AuditCheckAlertButton
                                    v-if="showReconciliationDiscrepancyAlertButton"
                                    auditCheckType="Bank"
                                    :unreconciledBalancesAccountNames="
                                        unreconciledBalancesAccountNames
                                    "
                                    @auditCheckAlertButtonClicked="onAuditCheckAlertButtonClick"
                                />
                                <BankReconciliationDiscrepancyDialog
                                    v-if="showBankReconciliationDiscrepancyDialog"
                                    v-model="showBankReconciliationDiscrepancyDialog"
                                    :accountingPeriodId="accountingPeriod?.id"
                                    :companyId="companyId"
                                    width="800px"
                                />
                                <v-btn-icon
                                    v-if="isHovering"
                                    :icon="vueGraph ? '$fa-euro-sign' : '$fa-chart-bar'"
                                    @click="vueGraph = !vueGraph"
                                    v-show="accounts.length"
                                    data-cy="CardBank-ChartOrEuroButton-ClickToggle"
                                >
                                </v-btn-icon>
                                <v-btn-icon
                                    v-if="isHovering"
                                    icon="$fa-search-plus"
                                    class="mr-1"
                                    @click="$emit('onVueDetailedChanged')"
                                    data-cy="CardBank-searhPlus"
                                >
                                </v-btn-icon>
                            </v-col>
                            <v-col
                                v-if="formattedTotalBalanceDate"
                                class="d-flex align-end justify-end"
                            >
                                <span>Solde au {{ formattedTotalBalanceDate }}</span>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card>
        </v-hover>
    </div>
</template>

<script lang="ts">
import gql from "graphql-tag";
import moment from "moment-timezone";
import { Component, Vue, toNative, Prop, Model } from "vue-facing-decorator";

import { mathFix } from "../services/mathServices";

import { Apollo } from "@/decorators";

import { Bar as BarChart } from "vue-chartjs";
import { TooltipItem } from "chart.js";

import { chartColors } from "../../../backend/src/common/chart/graphServices";
import { newCommonFormatCurrency } from "../../../backend/src/common/services/mathServices";

import { BankAccount } from "../../../types";
import { getDBFormattedEndDate, getDBFormattedStartDate } from "../services/dateServices";
import { getMostRecentAccountingPeriod } from "@/services/accountingPeriods";
import { formatDateForUI } from "@/helpers/date.helper";

import AuditCheckAlertButton from "@/views/audit/AuditCheckAlertButton.vue";
import BankReconciliationDiscrepancyDialog from "@/views/bank/BankReconciliationDiscrepancyDialog.vue";

import AUDIT_ITEMS from "@/graphql/audit/AuditItems.gql";
import { AuditCheckBankKey } from "@/types/auditCheck.types";

type BankAccountUI = BankAccount & { date: string };

/**
 * Compute the last reconciled balances for all accounts.
 * If all accounts have the same last reconciled balance date,
 * it sets the total last reconciled balance.
 * Also checks if all balances are reconciled.
 * @param this - The current context of the component.
 */
function computeLastReconciledBalances(this: any) {
    let haveLastReconciledBalancesSameDate = true;
    let previousLastReconciledBalanceDate;
    let totalBalance = 0;

    for (const account of this.accounts) {
        const auditItem = this.auditItemsAccountBalance.find((item) => item.id === account.id);

        if (auditItem) {
            account.balance = auditItem.lastReconciledBalance?.balance || 0;
            account.date = auditItem.lastReconciledBalance?.date;

            if (previousLastReconciledBalanceDate) {
                if (previousLastReconciledBalanceDate !== auditItem.lastReconciledBalance?.date) {
                    haveLastReconciledBalancesSameDate = false;
                }
            }

            totalBalance = totalBalance + (auditItem.lastReconciledBalance?.balance || 0);
            previousLastReconciledBalanceDate = auditItem.lastReconciledBalance?.date;
        }
    }

    if (haveLastReconciledBalancesSameDate) {
        this.totalLastReconciledBalance = {
            balance: totalBalance,
            date: previousLastReconciledBalanceDate,
        };
    } else {
        this.totalLastReconciledBalance = null;
    }

    this.areBalancesReconciled = this.auditItemsAccountBalance.every((i) => i.validated === true);
}

@Component({
    components: {
        AuditCheckAlertButton,
        BankReconciliationDiscrepancyDialog,
        BarChart,
    },
})
export class CardBank extends Vue {
    @Prop({ default: null }) companyId: string; // obligatoire
    @Prop({ default: false }) legend: boolean;
    @Prop({ default: false }) loading: boolean;

    @Model({ default: false, type: Boolean }) areBalancesReconciled: boolean;

    vueGraph = false;
    currentPalette = "cool";

    overlay = false;

    isTotalBalanceLoading = true;

    get startDate() {
        return moment().add(-30, "day"); // "2019-04-01T00:00:00Z" pour le test
    }

    get endDate() {
        return moment();
    }

    get formattedTotalBalanceDate(): string {
        const lastReconciledBalanceDate = this.totalLastReconciledBalance?.date;
        let formatted: string;

        if (lastReconciledBalanceDate) {
            formatted = formatDateForUI(lastReconciledBalanceDate);
        }

        return formatted;
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    shortedName(name) {
        let result = name;
        if (name.length > 10) {
            result = result.substring(0, 7) + "...";
        }
        return result;
    }

    @Apollo({
        query: gql`
            query BankAccountsQuery($companyId: ID!) {
                bankAccounts(where: { company: { id: $companyId } }) {
                    id
                    name
                    initialSyncDone
                    currentError
                    number
                    balances(orderBy: date_DESC, first: 1) {
                        balance
                    }
                    type
                    budgetInsightId
                }
            }
        `,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return !this.companyId;
        },
        debounce: 500,
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.bankAccounts) {
                    if (data.bankAccounts.length === 0) {
                        this.accounts = [{ index: 0, name: "", date: "", balance: 0, id: null }];
                    } else if (data.bankAccounts.length === 1) {
                        this.accounts = data.bankAccounts.map((e, ind) => ({
                            ...e,
                            index: ind,
                            date: null,
                            balance: null,
                        }));
                    } else {
                        // const total_balance = mathFix(
                        //     data.bankAccounts.reduce((acc, e) => acc + e.balance, 0),
                        // );
                        this.accounts = [
                            {
                                index: 0,
                                name: "Tous les comptes",
                                id: undefined,
                                date: null,
                                balance: null,
                            },
                            ...data.bankAccounts.map((e, ind) => ({
                                ...e,
                                index: ind + 1,
                                date: null,
                                balance: null,
                            })),
                        ];
                        this.vueGraph = true;
                    }
                    if (this.$route.params.bankAccountId) {
                        const index = this.accounts.findIndex(
                            (a) => a.id === this.$route.params.bankAccountId,
                        );
                        if (index >= 0) {
                            this.selected_account = 0;
                        }
                    }
                } else {
                    this.accounts = [];
                }
            }
        },
    })
    accounts: BankAccountUI[] = [{ index: 0, name: "", date: null, balance: 0, id: null } as any];

    totalLastReconciledBalance = null;

    get showReconciliationDiscrepancyAlertButton(): boolean {
        if (!this.auditItemsAccountBalance?.length || !this.isAccountingUser) {
            return false;
        }

        return this.auditItemsAccountBalance.some((i) => !i.validated);
    }

    get chartData() {
        if (this.accounts.length) {
            const label = [];
            const data = [];
            const dates = [];

            if (this.accounts.length == 1) {
                label.push(this.shortedName(this.accounts[0].name));
                data.push(this.accounts[0].balance);
                dates.push(this.accounts[0].date);
            }
            for (let indexAccounts = 1; indexAccounts < this.accounts.length; indexAccounts++) {
                if (this.accounts[indexAccounts].balance) {
                    // ne pas afficher les comptes égaux à 0
                    label.push(this.shortedName(this.accounts[indexAccounts].name));
                    data.push(this.accounts[indexAccounts].balance);
                    if (!this.formattedTotalBalanceDate) {
                        dates.push(this.accounts[indexAccounts].date);
                    }
                }
            }
            const datasets = [{ label: "balances", data: data }];
            chartColors("bar", datasets, this.currentPalette);
            const chartData = {
                labels: label,
                datasets: JSON.parse(JSON.stringify(datasets)),
                dates: dates,
            };
            return chartData;
        }
        return null;
    }

    // Used to display balances data (lastReconciledBalance)
    @Apollo({
        query: AUDIT_ITEMS,
        debounce: 500,
        variables() {
            return {
                companyId: this.companyId,
                auditCheckKey: AuditCheckBankKey.BankReconciliation,
                accountingPeriodId: this.accountingPeriod?.id,
            };
        },
        skip() {
            const shouldSkip = !this.companyId || !this.accounts?.length;
            
            if (!shouldSkip) {
                this.auditItemsAccountBalance = [];
                this.areBalancesReconciled = false;
                this.totalLastReconciledBalance = null;
                this.isTotalBalanceLoading = true;
            }
            
            return shouldSkip;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data?.auditItems?.items?.[0]) {
                this.auditItemsAccountBalance = data.auditItems.items[0].bankAccounts;

                computeLastReconciledBalances.call(this);

                this.isTotalBalanceLoading = false;
            }
        },
    })
    auditItemsAccountBalance: any;

    /**
     * Returns the names of the accounts with unreconciled balances
     */
    get unreconciledBalancesAccountNames(): string[] {
        return this.auditItemsAccountBalance?.filter((i) => !i.validated)?.map((i) => i.name);
    }

    // Used to display the total amount of debit and credit transactions
    @Apollo({
        query: gql`
            query BankTransactionAggregate(
                $companyId: ID!
                $startDate: DateTime!
                $endDate: DateTime!
            ) {
                bankTransactionAggregate(
                    companyId: $companyId
                    where: {
                        account: { company: { id: $companyId } }
                        dateDebit_gte: $startDate
                        dateDebit_lte: $endDate
                    }
                ) {
                    amountDebit
                    amountCredit
                    amountSum
                    count
                }
            }
        `,
        variables() {
            return {
                companyId: this.companyId,
                startDate: getDBFormattedStartDate(this.startDate),
                endDate: getDBFormattedEndDate(this.endDate),
            };
        },
        skip() {
            return !this.companyId;
        },
        debounce: 500,
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.bankTransactionAggregate) {
                    this.bankTransactionAggregate = data.bankTransactionAggregate;
                } else {
                    this.bankTransactionAggregate = {
                        amountDebit: 0,
                        amountCredit: 0,
                        amountSum: 0,
                        count: 0,
                    };
                }
            }
        },
    })
    bankTransactionAggregate = {
        amountDebit: 0,
        amountCredit: 0,
        amountSum: 0,
        count: 0,
    };

    get options() {
        const self = this;
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: this.legend,
                    position: "bottom",
                },
                tooltip: {
                    mode: "index",
                    position: "average",
                    callbacks: {
                        label: function (tooltipItem: TooltipItem<"bar">): string | string[] {
                            let label = tooltipItem.dataset
                                ? self
                                      .$t("dashboard.amountType." + tooltipItem.dataset.label)
                                      .toString()
                                : "";
                            if (label) {
                                label += " : ";
                            }
                            label += tooltipItem.dataset
                                ? self.newCommonFormatCurrency(
                                      tooltipItem.dataset.data[tooltipItem.dataIndex],
                                      undefined,
                                  )
                                : tooltipItem.formattedValue;
                            return label;
                        },
                        afterLabel: (tooltipItem: TooltipItem<"bar">): string | string[] => {
                            const additionalContent = self.chartData?.dates[tooltipItem.dataIndex];
                            if (additionalContent) {
                                const formattedDate = formatDateForUI(additionalContent);
                                if (formattedDate) {
                                    return "(au " + formattedDate + ")";
                                }
                            }
                            return "";
                        },
                    },
                },
            },
            scales: {
                y: {
                    stacked: true,
                    beginAtZero: true,
                    display: false,
                    grid: {
                        display: false,
                    },
                },
                x: {
                    stacked: true,
                    beginAtZero: true,
                    grid: {
                        display: false,
                    },
                },
            },
        };
    }

    showBankReconciliationDiscrepancyDialog = false;

    onAuditCheckAlertButtonClick() {
        this.showBankReconciliationDiscrepancyDialog = true;
    }

    /**
     * Get the selected accounting period from the store or the most recent one if none is selected
     */
    get accountingPeriod() {
        return (
            this.$store.state.accountingPeriod?.selectedAccountingPeriod ??
            this.getMostRecentAccountingPeriodFromStore()
        );
    }

    /**
     * Get the most recent accounting period from the store
     * @returns The most recent accounting period or null if none are found
     */
    getMostRecentAccountingPeriodFromStore() {
        const storedAccountingPeriods = this.$store.state.accountingPeriod?.accountingPeriods;
        if (!storedAccountingPeriods) {
            return null;
        }

        return getMostRecentAccountingPeriod(storedAccountingPeriods);
    }

    get isAccountingUser() {
        return this.$store.state.user.isAccountingUser;
    }
}

export default toNative(CardBank);
</script>
